/**
 * X-Digate Form Handler
 * Handles form validation and submission
 */ (function() {
    'use strict';
    // Form elements
    const contactForm = document.getElementById('contact-form');
    /**
     * Initialize form handling
     */ function init() {
        if (contactForm) setupContactForm();
    }
    /**
     * Setup contact form
     */ function setupContactForm() {
        contactForm.addEventListener('submit', handleSubmit);
        // Real-time validation
        const inputs = contactForm.querySelectorAll('.form-input, .form-textarea');
        inputs.forEach((input)=>{
            input.addEventListener('blur', ()=>validateField(input));
            input.addEventListener('input', ()=>{
                if (input.classList.contains('error')) validateField(input);
            });
        });
    }
    /**
     * Handle form submission
     */ async function handleSubmit(e) {
        e.preventDefault();
        const form = e.target;
        const submitBtn = form.querySelector('#submit-contactus-btn');
        // Validate all fields
        const isValid = validateForm(form);
        if (!isValid) {
            showNotification('Please fill in all required fields correctly.', 'error');
            return;
        }
        // Get form data
        const formData = new FormData(form);
        const data = Object.fromEntries(formData.entries());
        // Show loading state
        setLoadingState(submitBtn, true);
        try {
            // Submit form to API
            const response = await fetch('/api/contactus', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    name: data.name || '',
                    email: data.email || '',
                    message: data.message,
                    phone: data.phone
                })
            });
            if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
            // Success
            showNotification('Thank you! Your message has been sent successfully.', 'success');
            form.reset();
        } catch (error) {
            console.error('Form submission error:', error);
            showNotification('Sorry, there was an error sending your message. Please try again.', 'error');
        } finally{
            setLoadingState(submitBtn, false);
        }
    }
    /**
     * Validate entire form
     */ function validateForm(form) {
        const inputs = form.querySelectorAll('.form-input[required], .form-textarea[required]');
        let isValid = true;
        inputs.forEach((input)=>{
            if (!validateField(input)) isValid = false;
        });
        return isValid;
    }
    /**
     * Validate individual field
     */ function validateField(input) {
        const value = input.value.trim();
        const type = input.type;
        let isValid = true;
        let errorMessage = '';
        // Remove existing error
        removeError(input);
        // Required check
        if (input.required && !value) {
            isValid = false;
            errorMessage = 'This field is required';
        } else if (type === 'email' && value && !isValidEmail(value)) {
            isValid = false;
            errorMessage = 'Please enter a valid email address';
        } else if (input.minLength && value.length < input.minLength) {
            isValid = false;
            errorMessage = `Minimum ${input.minLength} characters required`;
        }
        if (!isValid) showError(input, errorMessage);
        return isValid;
    }
    /**
     * Check if email is valid
     */ function isValidEmail(email) {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return emailRegex.test(email);
    }
    /**
     * Show field error
     */ function showError(input, message) {
        input.classList.add('error');
        input.setAttribute('aria-invalid', 'true');
        // Create error message element
        const errorEl = document.createElement('span');
        errorEl.className = 'form-error';
        errorEl.setAttribute('role', 'alert');
        errorEl.textContent = message;
        errorEl.style.cssText = `
            display: block;
            color: #EF4444;
            font-size: 0.875rem;
            margin-top: 0.25rem;
        `;
        input.parentNode.appendChild(errorEl);
        // Add error styles to input
        input.style.borderColor = '#EF4444';
    }
    /**
     * Remove field error
     */ function removeError(input) {
        input.classList.remove('error');
        input.setAttribute('aria-invalid', 'false');
        input.style.borderColor = '';
        const errorEl = input.parentNode.querySelector('.form-error');
        if (errorEl) errorEl.remove();
    }
    /**
     * Set button loading state
     */ function setLoadingState(button, isLoading) {
        if (isLoading) {
            button.disabled = true;
            button.dataset.originalText = button.innerHTML;
            button.innerHTML = `
                <svg class="animate-spin" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                    <path d="M21 12a9 9 0 11-6.219-8.56"/>
                </svg>
                Sending...
            `;
        } else {
            button.disabled = false;
            button.innerHTML = button.dataset.originalText;
        }
    }
    /**
     * Show notification
     */ function showNotification(message, type = 'info') {
        // Remove existing notifications
        const existing = document.querySelector('.notification');
        if (existing) existing.remove();
        // Create notification element
        const notification = document.createElement('div');
        notification.className = `notification notification-${type}`;
        notification.setAttribute('role', 'alert');
        notification.setAttribute('aria-live', 'polite');
        const colors = {
            success: {
                bg: '#10B981',
                text: '#FFFFFF'
            },
            error: {
                bg: '#EF4444',
                text: '#FFFFFF'
            },
            info: {
                bg: '#3B82F6',
                text: '#FFFFFF'
            }
        };
        const { bg, text } = colors[type] || colors.info;
        notification.style.cssText = `
            position: fixed;
            top: 100px;
            right: 20px;
            max-width: 400px;
            padding: 16px 24px;
            background: ${bg};
            color: ${text};
            border-radius: 8px;
            box-shadow: 0 10px 25px rgba(0, 0, 0, 0.2);
            z-index: 10000;
            animation: slideIn 0.3s ease forwards;
            display: flex;
            align-items: center;
            gap: 12px;
        `;
        // Add icon based on type
        const icons = {
            success: '<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M22 11.08V12a10 10 0 11-5.93-9.14"/><path d="M22 4L12 14.01l-3-3"/></svg>',
            error: '<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="10"/><path d="M15 9l-6 6M9 9l6 6"/></svg>',
            info: '<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="10"/><path d="M12 16v-4M12 8h.01"/></svg>'
        };
        notification.innerHTML = `
            ${icons[type] || icons.info}
            <span>${message}</span>
            <button aria-label="Close notification" style="background: none; border: none; color: inherit; cursor: pointer; padding: 4px; margin-left: auto;">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M18 6L6 18M6 6l12 12"/></svg>
            </button>
        `;
        // Add animation keyframes
        const style = document.createElement('style');
        style.textContent = `
            @keyframes slideIn {
                from {
                    transform: translateX(100%);
                    opacity: 0;
                }
                to {
                    transform: translateX(0);
                    opacity: 1;
                }
            }
            @keyframes slideOut {
                from {
                    transform: translateX(0);
                    opacity: 1;
                }
                to {
                    transform: translateX(100%);
                    opacity: 0;
                }
            }
        `;
        document.head.appendChild(style);
        document.body.appendChild(notification);
        // Close button handler
        const closeBtn = notification.querySelector('button');
        closeBtn.addEventListener('click', ()=>dismissNotification(notification));
        // Auto dismiss after 5 seconds
        setTimeout(()=>dismissNotification(notification), 5000);
    }
    /**
     * Dismiss notification
     */ function dismissNotification(notification) {
        if (!notification || !notification.parentNode) return;
        notification.style.animation = 'slideOut 0.3s ease forwards';
        setTimeout(()=>{
            if (notification.parentNode) notification.remove();
        }, 300);
    }
    // Initialize on DOM ready
    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
    else init();
})();

//# sourceMappingURL=www.xdigate.ai.b151257d.js.map
